/**
 * Contract Import Dialog - Simplified
 *
 * Import contracts from supplier quotation files (xlsx/pdf)
 * Features:
 * - File upload with drag-and-drop
 * - AI extraction using Qwen
 * - Auto create and confirm POs
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  Upload,
  FileSpreadsheet,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import contractService from '../services/contractService';
import supplierService from '../services/supplierService';
import clientService from '../services/clientService';
import productService from '../services/productService';
import ContractImportReviewDialog from './ContractImportReviewDialog';
import ProductCreationDrawer from './ProductCreationDrawer';

function ContractImportDialog({
  open,
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [reviewContracts, setReviewContracts] = useState([]);

  // Supplier and client selection state
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [customPOName, setCustomPOName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingClients, setLoadingClients] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [errors, setErrors] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [creatingProductForIndex, setCreatingProductForIndex] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [createdProducts, setCreatedProducts] = useState([]);
  const [checkingExistingProduct, setCheckingExistingProduct] = useState({ index: null, loading: false });
  const [existingProductAlert, setExistingProductAlert] = useState(null);

  const fileInputRef = useRef(null);

  // Load suppliers and clients when dialog opens
  useEffect(() => {
    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      try {
        const data = await supplierService.getSuppliers();
        setSuppliers(data.suppliers || []);
      } catch (error) {
        console.error('Failed to load suppliers:', error);
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const loadClients = async () => {
      setLoadingClients(true);
      try {
        const data = await clientService.getClients();
        setClients(data.clients || []);
      } catch (error) {
        console.error('Failed to load clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    if (open) {
      loadSuppliers();
      loadClients();
    }
  }, [open]);

  // Custom Autocomplete component for supplier/client selection
  const AutocompleteSelect = ({ options, value, onChange, getOptionLabel, isOptionEqualToValue, label, error, helperText }) => {
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const selectedOption = options.find(opt => isOptionEqualToValue(opt, value));

    useEffect(() => {
      if (selectedOption) {
        setInputValue(getOptionLabel(selectedOption));
      } else if (value) {
        setInputValue(getOptionLabel(value));
      } else {
        setInputValue('');
      }
    }, [selectedOption, value, getOptionLabel]);

    const filteredOptions = options.filter(opt =>
      getOptionLabel(opt).toLowerCase().includes(inputValue.toLowerCase())
    );

    return (
      <div className="relative">
        <Label className="text-sm">{label}</Label>
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          className={`mt-1 ${error ? 'border-red-500' : ''}`}
        />
        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
            {filteredOptions.map((opt, idx) => (
              <div
                key={idx}
                className="px-3 py-2 hover:bg-muted cursor-pointer"
                onMouseDown={() => {
                  onChange(null, opt);
                  setIsOpen(false);
                }}
              >
                {getOptionLabel(opt)}
              </div>
            ))}
          </div>
        )}
        {helperText && (
          <p className={`text-xs mt-1 ${error ? 'text-destructive' : 'text-muted-foreground'}`}>{helperText}</p>
        )}
      </div>
    );
  };

  // Handler for PO Name change
  const handlePONameChange = (e) => {
    setCustomPOName(e.target.value);
    if (errors.customPOName) {
      setErrors(prev => ({ ...prev, customPOName: null }));
    }
  };

  // Handler for Client change
  const handleClientChange = (_, client) => {
    setSelectedClient(client);
    if (errors.client) {
      setErrors(prev => ({ ...prev, client: null }));
    }
  };


  // Validate required fields before import
  const validateFields = useCallback(() => {
    const newErrors = {};
    
    if (!customPOName.trim()) {
      newErrors.customPOName = '请输入采购订单名称';
    }
    
    if (!selectedSupplier) {
      newErrors.supplier = '请选择供应商';
    }
    
    if (!selectedClient) {
      newErrors.client = '请选择客户';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [customPOName, selectedSupplier, selectedClient]);

  const handleClose = useCallback(() => {
    setStep('upload');
    setFile(null);
    setUploading(false);
    setImporting(false);
    setError(null);
    setExtractionResult(null);
    setImportResult(null);
    setReviewContracts([]);
    // Reset supplier/client selection state
    setSuppliers([]);
    setClients([]);
    setSelectedSupplier(null);
    setSelectedClient(null);
    setCustomPOName('');
    setErrors({});
    onClose();
  }, [onClose]);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/pdf',
        'image/jpeg',
        'image/png',
      ];
      if (!validTypes.includes(selectedFile.type) &&
          !selectedFile.name.match(/\.(xlsx|xls|pdf|jpg|jpeg|png)$/i)) {
        setError('请上传 Excel 或 PDF 文件');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect({ target: { files: [droppedFile] } });
    }
  }, [handleFileSelect]);

  const handleCreateProductClick = useCallback(async (idx) => {
    const item = extractionResult?.items?.[idx];
    if (!item?.suggested_product_id) {
      setCreatingProductForIndex(idx);
      return;
    }

    setCheckingExistingProduct({ index: idx, loading: true });
    setExistingProductAlert(null);

    try {
      const existingProduct = await productService.findProductByProductId(item.suggested_product_id);
      
      if (existingProduct) {
        setExistingProductAlert({
          index: idx,
          product: existingProduct,
          message: `产品 "${existingProduct.product_id}" 已存在于数据库中，建议直接使用已存在的产品`
        });
      } else {
        setCreatingProductForIndex(idx);
      }
    } catch (err) {
      console.error('检查产品存在性失败:', err);
      setCreatingProductForIndex(idx);
    } finally {
      setCheckingExistingProduct({ index: null, loading: false });
    }
  }, [extractionResult]);

  const handleUseExistingProduct = useCallback((idx, product) => {
    if (extractionResult && idx !== null) {
      const updatedItems = [...extractionResult.items];
      updatedItems[idx] = {
        ...updatedItems[idx],
        matched_product_id: product._id,
        matched_product_code: product.product_id || product._id
      };
      setExtractionResult({
        ...extractionResult,
        items: updatedItems
      });
    }
    setExistingProductAlert(null);
  }, [extractionResult]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      setError('请先选择文件');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await contractService.extractContract(file);
      if (result.success) {
        setExtractionResult(result);
        setStep('import');
        // Reset form fields for new file
        setCustomPOName('');
        setSelectedSupplier(null);
        setSelectedClient(null);
        setErrors({});
      } else {
        setError(result.error || '文件解析失败');
      }
    } catch (err) {
      setError(err.response?.data?.error || '文件上传失败');
    } finally {
      setUploading(false);
    }
  }, [file]);

  const proceedWithImport = useCallback(async (contracts) => {
    setImporting(true);
    setError(null);

    try {
      const result = await contractService.importContracts(contracts);
      if (result.success) {
        setImportResult(result);
        setStep('done');
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        setError(result.error || '导入失败');
      }
    } catch (err) {
      setError(err.response?.data?.error || '导入失败');
    } finally {
      setImporting(false);
    }
  }, [onSuccess]);

  const handleImport = useCallback(async (extractionResult) => {
    // Run validation before importing
    if (!validateFields()) {
      return;
    }

    const items = extractionResult?.items || [];
    const matchedCount = items.filter(i => i.matched_product_id).length;

    // If there are unmatched items, show review dialog directly
    if (matchedCount < items.length) {
      const contractData = [{
        po_name: customPOName.trim(),
        supplier_id: selectedSupplier?._id || selectedSupplier?.id,
        supplier_name: selectedSupplier?.supplier_name || extractionResult.supplier_name,
        supplier_contact: selectedSupplier?.contact_info || {},
        client_id: selectedClient?.client_id || selectedClient?.client_code,
        client_ref: selectedClient?._id || selectedClient?.id,
        client_name: selectedClient?.client_name || selectedClient?.name,
        quote_date: extractionResult.quote_date,
        items: items,
        source_file: file.name,
      }];
      setReviewContracts(contractData);
      setStep('review');
      return;
    }

    // All items matched, proceed with import
    const contractData = [{
      po_name: customPOName.trim(),
      supplier_id: selectedSupplier?._id || selectedSupplier?.id,
      supplier_name: selectedSupplier?.supplier_name || extractionResult.supplier_name,
      supplier_contact: selectedSupplier?.contact_info || {},
      client_id: selectedClient?.client_id || selectedClient?.client_code,
      client_ref: selectedClient?._id || selectedClient?.id,
      client_name: selectedClient?.client_name || selectedClient?.name,
      quote_date: extractionResult.quote_date,
      items: extractionResult.items,
      source_file: file.name,
    }];
    await proceedWithImport(contractData);
  }, [file, proceedWithImport, validateFields, customPOName, selectedSupplier, selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReviewConfirm = useCallback(async (updatedContracts) => {
    // Merge selection data into updated contracts from review step
    const contractsWithSelections = updatedContracts.map(contract => ({
      ...contract,
      po_name: customPOName.trim(),
      supplier_id: selectedSupplier?._id || selectedSupplier?.id,
      supplier_name: selectedSupplier?.supplier_name || extractionResult.supplier_name,
      supplier_contact: selectedSupplier?.contact_info || {},
      client_id: selectedClient?.client_id || selectedClient?.client_code,
      client_ref: selectedClient?._id || selectedClient?.id,
      client_name: selectedClient?.client_name || selectedClient?.name,
      source_file: file.name,
    }));
    setStep('import');
    await proceedWithImport(contractsWithSelections);
  }, [proceedWithImport, customPOName, selectedSupplier, selectedClient, extractionResult, file]);

  const getMatchBadge = (item) => {
    if (!item.matched_product_id) {
      return <span className="text-yellow-600 text-sm">待匹配</span>;
    }
    return <span className="text-green-600 text-sm">已匹配</span>;
  };

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${file ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary'}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelect}
          className="hidden"
        />
        {file ? (
          <div className="flex flex-col items-center gap-2">
            {file.name.match(/\.(xlsx|xls)$/i) ? (
              <FileSpreadsheet className="h-12 w-12 text-green-600" />
            ) : file.name.match(/\.pdf$/i) ? (
              <FileText className="h-12 w-12 text-red-600" />
            ) : (
              <FileText className="h-12 w-12 text-blue-600" />
            )}
            <p className="font-medium">{file.name}</p>
            <p className="text-sm text-muted-foreground">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg font-medium">拖拽文件到此处或点击选择</p>
            <p className="text-sm text-muted-foreground">
              支持 Excel (.xlsx, .xls) 和 PDF 文件
            </p>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleClose}>
          取消
        </Button>
        <Button onClick={handleUpload} disabled={!file || uploading}>
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              解析中...
            </>
          ) : (
            '开始解析并导入'
          )}
        </Button>
      </div>
    </div>
  );

  const renderImportStep = () => {
    const items = extractionResult?.items || [];
    const matchedCount = items.filter(i => i.matched_product_id).length;

    return (
      <div className="space-y-4">
        {/* New form fields - BEFORE the item table */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Custom PO Name */}
          <div className="space-y-2">
            <Label className="text-sm">采购订单名称 *</Label>
            <Input
              value={customPOName}
              onChange={handlePONameChange}
              placeholder="输入采购订单名称"
              className={errors.customPOName ? 'border-red-500' : ''}
            />
            {errors.customPOName && (
              <p className="text-xs text-destructive">{errors.customPOName}</p>
            )}
          </div>

          {/* Supplier Selection - Dropdown */}
          <div className="space-y-2">
            <Label className="text-sm">供应商 *</Label>
            <Select
              value={selectedSupplier?._id || ''}
              onValueChange={(value) => {
                const supplier = suppliers.find(s => (s._id || s.id) === value);
                setSelectedSupplier(supplier || null);
                if (errors.supplier) {
                  setErrors(prev => ({ ...prev, supplier: null }));
                }
              }}
            >
              <SelectTrigger className={errors.supplier ? 'border-red-500' : ''}>
                <SelectValue placeholder="选择供应商" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.length === 0 && (
                  <SelectItem value="_empty" disabled>暂无供应商数据</SelectItem>
                )}
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier._id || supplier.id} value={supplier._id || supplier.id}>
                    {supplier.supplier_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.supplier && (
              <p className="text-xs text-destructive">{errors.supplier}</p>
            )}
          </div>

          {/* Client Selection */}
          <AutocompleteSelect
            options={clients}
            value={selectedClient}
            onChange={handleClientChange}
            getOptionLabel={(option) => `${option?.client_name || option?.name || ''} (${option?.client_id || option?.client_code || ''})`}
            isOptionEqualToValue={(option, value) => (option?._id || option?.id) === (value?._id || value?.id)}
            label="客户 *"
            error={!!errors.client}
            helperText={errors.client}
          />
        </div>

        {/* Existing supplier info display */}
        <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
          <div className="flex-1">
            <p className="text-sm font-medium">供应商</p>
            <p className="text-lg">{extractionResult?.supplier_name || '未知'}</p>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">提取物料数</p>
            <p className="text-lg">{items.length}</p>
          </div>
          <div className="flex gap-2">
            <span className="text-green-600 text-sm">{matchedCount} 已匹配</span>
            <span className="text-yellow-600 text-sm">{items.length - matchedCount} 待匹配</span>
          </div>
        </div>

        <div className="border rounded-lg max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="text-left p-3">物料名称</th>
                <th className="text-left p-3">规格</th>
                <th className="text-right p-3">单价</th>
                <th className="text-center p-3">匹配状态</th>
                <th className="text-center p-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="p-3">{item.material_name}</td>
                  <td className="p-3">{item.specification || '-'}</td>
                  <td className="p-3 text-right">
                    ¥{item.unit_price?.toFixed(2) || '0.00'}
                  </td>
                  <td className="p-3 text-center">
                    {getMatchBadge(item)}
                  </td>
                  <td className="p-3 text-center">
                    {existingProductAlert?.index === idx ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-orange-600">产品已存在</span>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleUseExistingProduct(idx, existingProductAlert.product)}
                        >
                          使用已有
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCreateProductClick(idx)}
                        disabled={checkingExistingProduct.index === idx}
                      >
                        {checkingExistingProduct.index === idx ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            检查中
                          </>
                        ) : (
                          '创建'
                        )}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Existing Product Alert */}
        {existingProductAlert && (
          <Alert className="bg-orange-50 border-orange-200">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              {existingProductAlert.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Product Creation Drawer */}
        <ProductCreationDrawer
          open={creatingProductForIndex !== null}
          onClose={() => setCreatingProductForIndex(null)}
          onProductCreated={(product) => {
            setCreatedProducts(prev => [...prev, product]);
            
            // Auto-match the created product to the current row
            if (extractionResult && creatingProductForIndex !== null) {
              const updatedItems = [...extractionResult.items];
              updatedItems[creatingProductForIndex] = {
                ...updatedItems[creatingProductForIndex],
                matched_product_id: product._id,
                matched_product_code: product.product_id || product._id
              };
              setExtractionResult({
                ...extractionResult,
                items: updatedItems
              });
            }
            
            setCreatingProductForIndex(null);
          }}
          initialData={creatingProductForIndex !== null ? {
            name: items[creatingProductForIndex].material_name || '',
            specification: items[creatingProductForIndex].specification || '',
            product_id: items[creatingProductForIndex].suggested_product_id || '',
            unit_price: items[creatingProductForIndex].unit_price || '',
            unit: items[creatingProductForIndex].unit || '支',
            category: '原材料',
            tip_diameter: items[creatingProductForIndex].tip_diameter || '',
            total_length: items[creatingProductForIndex].total_length || '',
            product_type: 'raw_material'
          } : {}}
        />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            匹配成功的物料将直接创建采购订单
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={importing}>
              取消
            </Button>
            <Button 
              onClick={() => handleImport(extractionResult)} 
              disabled={importing || !customPOName.trim() || !selectedSupplier || !selectedClient}
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  导入中...
                </>
              ) : (
                '确认导入'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderDoneStep = () => (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-4 p-8">
        <CheckCircle2 className="h-16 w-16 text-green-600" />
        <h3 className="text-xl font-semibold">采购订单创建成功</h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-muted rounded-lg text-center">
          <p className="text-3xl font-bold">{importResult?.created_count || 0}</p>
          <p className="text-sm text-muted-foreground">已创建的采购订单</p>
        </div>
        <div className="p-4 bg-muted rounded-lg text-center">
          <p className="text-3xl font-bold">{importResult?.updated_count || 0}</p>
          <p className="text-sm text-muted-foreground">已更新物料价格</p>
        </div>
      </div>

      {importResult?.unmatched_count > 0 && (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {importResult.unmatched_count} 项物料未匹配到产品，已跳过
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-center gap-2">
        <Button variant="outline" onClick={handleClose}>
          关闭
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>导入合同</DialogTitle>
          <DialogDescription>
            上传供应商报价文件，自动提取数据并创建采购订单
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && renderUploadStep()}
        {step === 'import' && renderImportStep()}
        {step === 'review' && (
          <ContractImportReviewDialog
            open={true}
            onClose={() => setStep('import')}
            contracts={reviewContracts}
            onConfirm={handleReviewConfirm}
            isCreating={importing}
          />
        )}

        {step === 'done' && renderDoneStep()}
      </DialogContent>
    </Dialog>
  );
}

ContractImportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ContractImportDialog;
